import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Select,
  Text,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  createQuickCashflowAiConfigVersion,
  DEFAULT_QUICK_CASHFLOW_EXTRACTION_PROMPT,
  getLatestQuickCashflowAiConfig,
  getQuickCashflowAiConfigVersion,
  listQuickCashflowAiConfigVersions,
  QUICK_CASHFLOW_ALLOWED_MODELS,
} from 'api/quickCashflowAiConfigApi';
import ContentBox from 'components/ContentBox';
import { useEffect, useState } from 'react';

function QuickCashflowAiConfigAdmin() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [summaries, setSummaries] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState('edit');
  const [viewDoc, setViewDoc] = useState(null);
  const [draftPrompt, setDraftPrompt] = useState('');
  const [draftExtraction, setDraftExtraction] = useState('');
  const [draftModel, setDraftModel] = useState('gpt-4o');
  const [saveNote, setSaveNote] = useState('');
  const [saving, setSaving] = useState(false);

  const bootstrap = async () => {
    setLoading(true);
    try {
      let latest = null;
      try {
        latest = await getLatestQuickCashflowAiConfig();
      } catch (err) {
        if (err?.response?.status !== 404) {
          throw err;
        }
      }
      const sum = await listQuickCashflowAiConfigVersions();
      setSummaries(sum || []);
      setSelectedVersion('edit');
      setViewDoc(null);
      setDraftPrompt(latest?.systemPrompt || '');
      setDraftExtraction(
        latest?.extractionPrompt || DEFAULT_QUICK_CASHFLOW_EXTRACTION_PROMPT
      );
      setDraftModel(latest?.model || 'gpt-4o');
    } catch (e) {
      toast({
        title: 'Error',
        description: e?.response?.data?.error || e.message,
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    bootstrap();
  }, []);

  const onVersionChange = async (value) => {
    setSelectedVersion(value);
    if (value === 'edit') {
      setViewDoc(null);
      return;
    }
    const doc = await getQuickCashflowAiConfigVersion(Number(value));
    setViewDoc(doc);
  };

  const onSave = async () => {
    if (!draftPrompt.trim()) {
      toast({
        title: 'System prompt required',
        status: 'warning',
      });
      return;
    }
    if (!draftExtraction.trim()) {
      toast({
        title: 'Extraction spec required',
        status: 'warning',
      });
      return;
    }
    setSaving(true);
    try {
      await createQuickCashflowAiConfigVersion({
        systemPrompt: draftPrompt.trim(),
        extractionPrompt: draftExtraction.trim(),
        model: draftModel,
        note: saveNote.trim() || undefined,
      });
      toast({ title: 'Saved new version', status: 'success' });
      setSaveNote('');
      await bootstrap();
    } catch (e) {
      toast({
        title: 'Save failed',
        description: e?.response?.data?.error || e.message,
        status: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const readOnly = selectedVersion !== 'edit';
  const displayPrompt = readOnly ? viewDoc?.systemPrompt : draftPrompt;
  const displayExtraction = readOnly
    ? viewDoc?.extractionPrompt
    : draftExtraction;
  const displayModel = readOnly ? viewDoc?.model : draftModel;

  return (
    <ContentBox loading={loading} title="Quick Cash Flow AI">
      <VStack align="stretch" spacing={6}>
        <Text color="gray.600" fontSize="sm">
          System prompt, extraction spec, and model used by the Quick Cash Flow
          assistant when extracting opex, gross margin, cash balance, and ROAS
          vectors from uploaded files.
        </Text>
        <HStack>
          <FormControl maxW="280px">
            <FormLabel>Version</FormLabel>
            <Select
              value={selectedVersion}
              onChange={(e) => onVersionChange(e.target.value)}
            >
              <option value="edit">Edit (from latest)</option>
              {(summaries || []).map((s) => (
                <option key={s.version} value={String(s.version)}>
                  v{s.version}
                  {s.note ? ` — ${s.note}` : ''}
                </option>
              ))}
            </Select>
          </FormControl>
        </HStack>
        <FormControl>
          <FormLabel>Model</FormLabel>
          <Select
            value={displayModel || 'gpt-4o'}
            onChange={(e) => setDraftModel(e.target.value)}
            isDisabled={readOnly}
            maxW="280px"
          >
            {QUICK_CASHFLOW_ALLOWED_MODELS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>System prompt</FormLabel>
          <Textarea
            value={displayPrompt || ''}
            onChange={(e) => setDraftPrompt(e.target.value)}
            isReadOnly={readOnly}
            minH="160px"
            fontFamily="mono"
            fontSize="sm"
          />
        </FormControl>
        <FormControl>
          <FormLabel>Extraction spec</FormLabel>
          <Textarea
            value={displayExtraction || ''}
            onChange={(e) => setDraftExtraction(e.target.value)}
            isReadOnly={readOnly}
            minH="240px"
            fontFamily="mono"
            fontSize="sm"
          />
        </FormControl>
        {!readOnly && (
          <>
            <FormControl>
              <FormLabel>Version note (optional)</FormLabel>
              <Textarea
                value={saveNote}
                onChange={(e) => setSaveNote(e.target.value)}
                placeholder="What changed in this version?"
              />
            </FormControl>
            <Button colorScheme="purple" onClick={onSave} isLoading={saving}>
              Save as new version
            </Button>
          </>
        )}
        {readOnly && viewDoc && (
          <Text fontSize="sm" color="gray.500">
            Created {new Date(viewDoc.createdAt).toLocaleString()} by{' '}
            {viewDoc.createdBy || 'unknown'}
          </Text>
        )}
      </VStack>
    </ContentBox>
  );
}

export default QuickCashflowAiConfigAdmin;
